import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, CircleCheckBig } from 'lucide-react'
import { cn } from '@playbooks/ui/lib/utils'
import { ActionCardsElement } from '@playbooks/action-cards'
import { Button } from '@playbooks/ui/components/button'
import { ViewMode } from '@playbooks/action-cards-utils/src'

import { SliderManager, Step, StepType, WebPlaySettings } from '../types'
import { isColorDark } from '../utils/isColorDark'
import { toast } from 'sonner'

interface SliderNavigationProps {
  viewMode: ViewMode
  accentColor: string
  hasSections: boolean
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  openSheet: () => void
  sliderManagerRef: React.RefObject<SliderManager | null>
  isFinishButtonVisible: boolean
  webplaySettings: WebPlaySettings
}

const SliderNavigation = ({
  viewMode,
  accentColor,
  hasSections,
  actionCardsElementRef,
  openSheet,
  sliderManagerRef,
  isFinishButtonVisible,
  webplaySettings,
}: SliderNavigationProps) => {
  const { t } = useTranslation()
  const [slide, setSlide] = useState<Step | null>(null)

  const isAccentColorDark = isColorDark(accentColor)

  const isFirst = slide?.type === StepType.FIRST || slide === null
  const isLast = slide?.type === StepType.LAST
  const isBrowHidden = slide?.type !== StepType.LAST && viewMode === 'funnel'

  const handleNextBtnClick = () => {
    const currentStep = sliderManagerRef.current?.getCurrentStep?.()
    const slidesData = actionCardsElementRef.current?.getSlidesData?.()

    if (!slidesData?.slides) {
      return
    }

    const { slides, canProceed } = slidesData

    if (!canProceed) {
      toast.warning(
        t(
          'submit.incompleteEmailFields',
          'You have incomplete email fields. Check the data you entered and try again.',
        ),
      )
    }

    if (slides && currentStep) {
      const slide = slides[currentStep.step]

      actionCardsElementRef.current?.highlightElementIds?.(slide?.requiredDataIds || [])

      if (!slides[currentStep.step]?.isFilled || !canProceed) return
    }

    setSlide(sliderManagerRef.current?.nextSlide?.() || null)
  }

  const handlePrevBtnClick = () => {
    const slidesData = actionCardsElementRef.current?.getSlidesData?.()

    if (!slidesData) {
      return
    }

    if (!slidesData.canProceed) {
      actionCardsElementRef.current?.highlightElementIds?.([])

      toast.warning(
        t(
          'submit.incompleteEmailFields',
          'You have incomplete email fields. Check the data you entered and try again.',
        ),
      )

      return
    }

    setSlide(sliderManagerRef.current?.prevSlide?.() || null)
  }

  return (
    <div
      className={cn('relative flex h-9 w-full', !hasSections && 'hidden', !webplaySettings.enableFunnelView && 'h-0')}
    >
      <SliderButton
        isLeft
        hidden={isFirst}
        onClick={handlePrevBtnClick}
        accentColor={accentColor}
        isAccentColorDark={isAccentColorDark}
        viewMode={viewMode}
      >
        {t('submit.backBtn', 'Back')}
      </SliderButton>
      <div className="ml-auto">
        <SliderButton
          hidden={isLast}
          onClick={handleNextBtnClick}
          accentColor={accentColor}
          isAccentColorDark={isAccentColorDark}
          viewMode={viewMode}
        >
          {t('submit.nextBtn', 'Next')}
        </SliderButton>
      </div>
      <Button
        type="button"
        onClick={openSheet}
        style={{ background: accentColor, borderColor: isAccentColorDark ? undefined : accentColor }}
        className={cn(
          !isFinishButtonVisible ? 'hidden' : 'flex',
          !isBrowHidden && 'top-0',
          !webplaySettings.enableFunnelView && '-top-15',
          isAccentColorDark && 'border-zinc-800! border',
          'absolute left-1/2 -translate-x-[50%] transition-[top] z-100',
          'gap-2 items-center justify-center px-4',
          'text-white',
        )}
      >
        <CircleCheckBig size={16} />

        <span>{t('submit.browLabel', 'Complete')}</span>
      </Button>
    </div>
  )
}

interface SliderButtonProps {
  hidden: boolean
  isLeft?: boolean
  children: React.ReactNode
  onClick: () => void
  viewMode: string
  accentColor: string
  isAccentColorDark: boolean
}

const SliderButton = ({
  isLeft,
  hidden,
  children,
  viewMode,
  accentColor,
  isAccentColorDark,
  onClick,
}: SliderButtonProps) => {
  if (viewMode !== 'funnel') {
    return null
  }

  return hidden ? null : (
    <Button
      onClick={onClick}
      style={{ background: accentColor, borderColor: isAccentColorDark ? undefined : accentColor }}
      className={cn(
        'flex items-center justify-center text-white hover:bg-wp-dark px-4',
        'dark:border dark:border-white gap-2',

        isAccentColorDark && 'border-zinc-800! border border-solid',
      )}
    >
      {isLeft && (
        <div style={{ transform: 'rotate(180deg)' }}>
          <ArrowRight />
        </div>
      )}

      {children}

      {!isLeft && (
        <div>
          <ArrowRight />
        </div>
      )}
    </Button>
  )
}

export default SliderNavigation
